import React, { useState, useEffect, useRef } from "react"
import { Routes, Route, Switch, withRouter } from "react-router-dom"
import { SkewLoader } from "react-spinners"
import request from "request"

import Modal from "Modal"
import Button from "Button"
import Input from "Input"
import Flash from "flash"

const useFocusTracking = () => {
  const [focusedElementId, setFocusedElementId] = useState(null)

  useEffect(() => {
    // Function to update the focused element ID
    const handleFocusChange = () => {
      setFocusedElementId(document.activeElement?.id || null)
    }

    // Add event listeners for focus and blur events
    document.addEventListener('focusin', handleFocusChange)
    document.addEventListener('focusout', handleFocusChange)

    // Set initial state
    handleFocusChange()

    // Clean up event listeners
    return () => {
      document.removeEventListener('focusin', handleFocusChange)
      document.removeEventListener('focusout', handleFocusChange)
    }
  }, []) // Empty dependency array since we're setting up listeners

  return focusedElementId
}

const AddCreditModal = ({
  isOpen,
  onClose,
  order,
  refreshOrder,
}) => {
  const [scannedValue, setScannedValue] = useState("")
  const [manualEntry, setManualEntry] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const focusedElementId = useFocusTracking()
  const inputRef = useRef(null)

  // Clear states and close modal
  const handleClose = () => {
    setScannedValue("")
    setManualEntry("")
    setIsLoading(false)
    onClose()
  }

  // Handle the barcode scan submission
  const handleBarcodeSubmit = async (barcode) => {
    // Only process if we have a value and not already loading
    if (barcode.trim() && !isLoading) {
      setIsLoading(true)

      const req = request.post(`/carts/${order.id}/prepaid_credits/`, {
        code: barcode.trim()
      })

      req.then(({data}) => {
        if(data.success) {
          Flash.broadcast("success", "Credit Applied")
          refreshOrder()
          handleClose()
        } else {
          Flash.broadcast("info", "Something went wrong, try again")
        }
      })
      req.catch(({response}) => {
        Flash.broadcast("error", response.data.errors[0])
      })
      req.finally(() => {
        setScannedValue("")
        setIsLoading(false)
        if (inputRef.current) {
          inputRef.current.focus()
        }
      })
    }
  }

  // Mock function for processing barcode - replace with actual implementation
  const processBarcodeRequest = async (barcode) => {

    // Simulate API delay
    return
  }

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value
    setScannedValue(value)
  }
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && scannedValue.trim()) {
      e.preventDefault()
      handleBarcodeSubmit(scannedValue)
    }
  }

  // Handle manual entry submission
  const handleManualSubmit = () => {
    if (manualEntry.trim()) {
      handleBarcodeSubmit(manualEntry)
      setManualEntry("")
    }
  }

  // Focus management effect
  useEffect(() => {
    // Only run effect if modal is open
    if (!isOpen) return

    // Use a small timeout to ensure the DOM is fully rendered
    const focusTimeout = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 100)

    return () => {
      clearTimeout(focusTimeout)
    }
  }, [isOpen])

  return (
    <Modal
      title="Add Credits - Scan Barcode"
      open={isOpen}
      toggleModal={handleClose}
      actions={
        <>
          <Button text="Cancel" color="red" onClick={handleClose} disabled={isLoading} />
        </>
      }
    >
      <div>
        {/* Hidden input for barcode scanner */}
        <input
          ref={inputRef}
          id="barcodeInput"
          type="text"
          value={scannedValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          autoFocus={true}
          style={{ position: 'absolute', top: '-9999px', left: '-9999px', opacity: 0 }}
          aria-label="Barcode Scanner Input"
          disabled={isLoading}
        />

        {isLoading ? (
          <div className="mt-4 has-text-centered">
            <p className="is-size-3">Looking up Credit...</p>
            <div className="my-4">
              <SkewLoader size={40} color="#F9690E" />
            </div>
          </div>
        ) : (
          <>
            <div className="text-sm text-gray-500 mt-2">
              { inputRef.current?.id === focusedElementId &&
              <div>
                <h3 className="is-size-3 has-text-centered">
                  Ready to scan...
                </h3>
              </div>
              }
            </div>

            <div className="mt-4">
              <div className="has-text-centered mb-3">
                <p className="is-size-5 has-text-weight-semibold">Or enter code manually:</p>
              </div>

              <div className="field has-addons">
                <div className="control is-expanded">
                  <Input
                    placeholder="Enter credit code"
                    value={manualEntry}
                    onChange={setManualEntry}
                  />
                </div>
                <div className="control">
                  <Button
                    text="Submit"
                    color="primary"
                    onClick={handleManualSubmit}
                    disabled={!manualEntry.trim()}
                  />
                </div>
              </div>
            </div>
          </>
        )}

      </div>
    </Modal>
  )
}

export default AddCreditModal
